import { useEffect } from 'react'
import {
  CLE_RECHARGEMENT,
  decisionRechargement,
  doitDemanderFenetre,
  doitRecharger,
  PLAFOND_REPORT_MS,
} from './mise-a-jour'
import {
  demanderFenetreRechargement,
  raisonsOccupation,
  surChangementOccupation,
  surInactivite,
} from './occupation'

// Barrière de version (portée de flowAI, src/app/app/UpdateGate.tsx). Compare la version du
// paquet à /version.json (jamais en cache) au montage, au retour au premier plan et chaque
// minute. Écart : nouveau déploiement, on purge les caches du service worker et on recharge,
// avec une garde anti-boucle. Tant que l'appli garde un travail (pages choisies, envoi en
// cours), le rechargement attend ; au-delà d'un plafond, il passe si seule la file d'envoi
// le retient (ses octets sont dans IndexedDB), jamais au milieu d'un envoi.

const VERSION = __VERSION_APPLI__
const INTERVALLE_MS = 60_000

export function BarriereVersion(): null {
  useEffect(() => {
    if (VERSION === 'dev') return
    let annule = false
    let rechargement = false
    let attente: { cible: string; depuis: number } | null = null
    let desabonnements: (() => void)[] = []
    let minuteurPlafond: number | null = null

    const oublierAttente = () => {
      for (const d of desabonnements) d()
      desabonnements = []
      if (minuteurPlafond !== null) window.clearTimeout(minuteurPlafond)
      minuteurPlafond = null
    }
    const possibleMaintenant = () =>
      decisionRechargement(raisonsOccupation(), attente?.depuis ?? Date.now(), Date.now()) ===
      'recharger'

    const purgerCaches = async () => {
      try {
        if (typeof caches === 'undefined') return
        await Promise.all((await caches.keys()).map((k) => caches.delete(k)))
      } catch {
        /* au mieux */
      }
    }

    const recharger = async (cible: string) => {
      if (rechargement || annule) return
      if (!possibleMaintenant()) {
        demander(cible)
        return
      }
      rechargement = true
      oublierAttente()
      await purgerCaches()
      // Des pages ont pu être choisies pendant la purge : on revérifie avant de les perdre.
      if (annule || !possibleMaintenant()) {
        rechargement = false
        demander(cible)
        return
      }
      try {
        window.sessionStorage.setItem(CLE_RECHARGEMENT, cible)
      } catch {
        /* ignoré */
      }
      window.location.reload()
    }

    const reevaluer = () => {
      if (!attente || rechargement || annule) return
      if (possibleMaintenant()) void recharger(attente.cible)
      else if (doitDemanderFenetre(raisonsOccupation(), attente.depuis, Date.now()))
        demanderFenetreRechargement()
    }

    function demander(cible: string) {
      if (rechargement || annule) return
      if (attente) attente.cible = cible
      else attente = { cible, depuis: Date.now() }
      if (possibleMaintenant()) {
        void recharger(cible)
        return
      }
      if (doitDemanderFenetre(raisonsOccupation(), attente.depuis, Date.now()))
        demanderFenetreRechargement()
      if (desabonnements.length === 0)
        desabonnements = [
          surInactivite(() => window.setTimeout(reevaluer, 0)),
          surChangementOccupation(() => window.setTimeout(reevaluer, 0)),
        ]
      if (minuteurPlafond === null)
        minuteurPlafond = window.setTimeout(
          () => {
            minuteurPlafond = null
            reevaluer()
          },
          Math.max(0, attente.depuis + PLAFOND_REPORT_MS - Date.now()),
        )
    }

    const verifier = async () => {
      if (annule || rechargement) return
      try {
        const r = await fetch('/version.json', { cache: 'no-store' })
        if (!r.ok) return
        const serveur = ((await r.json()) as { version?: string }).version
        let dejaTentee: string | null = null
        try {
          dejaTentee = window.sessionStorage.getItem(CLE_RECHARGEMENT)
        } catch {
          dejaTentee = null
        }
        if (doitRecharger(VERSION, serveur, dejaTentee)) demander(serveur!)
        else if (attente) {
          // Le serveur n'annonce plus de version plus récente (retour arrière) : on oublie.
          attente = null
          oublierAttente()
        }
      } catch {
        /* réseau coupé : prochain cycle */
      }
    }

    const auPremierPlan = () => {
      if (document.visibilityState === 'visible') void verifier()
    }
    void verifier()
    document.addEventListener('visibilitychange', auPremierPlan)
    window.addEventListener('focus', auPremierPlan)
    const intervalle = window.setInterval(() => void verifier(), INTERVALLE_MS)
    return () => {
      annule = true
      document.removeEventListener('visibilitychange', auPremierPlan)
      window.removeEventListener('focus', auPremierPlan)
      window.clearInterval(intervalle)
      oublierAttente()
    }
  }, [])
  return null
}
